import { Yrke } from '../AnsokanForms/LegAnsokanForm.types';
import React from 'react';
import { FormField } from 'sos-common/js/commonTypes';
import { validateField } from './publicform.validation.service';
import { ValidationError } from '../IntygApplication/Types/Intyg.types';

// Needs to match:
// Legitimation.Business.EditorDescriptors.OpenWebAnsokanFormPageTypeOfAnsokanSelectionFactory
export enum TypeOfAnsokan {
  Legitimation,
  Specialistkompetens,
}
export enum OutSideEuEesType {
  None = 0,
  OutsideEuEes = 1,
  Assessment = 2,
}
// Needs to match:
// Legitimation.Models.Pocos.YrkeKostnad
export interface YrkeKostnad {
  Yrke: string;
  Kostnad: number;
}

// Needs to match:
// Legitimation.Models.Pocos.OpenWebAnsokanEpiTexts
export interface OpenWebAnsokanEpiTexts {
  SpecialistsjukskoterskaHeading: string;
  SpecialistsjukskoterskaPreamble: string;
  SpecialistsjukskoterskaBody: string;
  Preamble: string;
  SpecialistkompetensHeading: string;
  SpecialistkompetensPreamble: string;
  SpecialistkompetensBody: string;
  SpecialistkompetensTandlakareBody: string;

  BetalningHeading: string;
  BetalningPreamble: string;
  BetalningBody: string;
  BetalningBodyLakare: string;
  BetalningBoxHeading: string;
  BetalningBoxBody: string;

  UtbildningHeading: string;
  UtbildningPreamble: string;
  UtbildningBody: string;

  DokumentHeading: string;
  DokumentPreamble: string;
  DokumentInforuta: string;
  DokumentBody: string;
  DokumentBoxHeading: string;
  DokumentBoxBody: string;

  PersonuppgifterHeading: string;
  PersonuppgifterPreamble: string;
  PersonuppgifterBody: string;

  SamtyckeLabel: string;
  ReadmorePersonuppgifter: string;

  DiarienummerHeading: string;
}

// Needs to match:
// Legitimation.Models.Pocos.OpenWebAnsokanInfoBubbles
export interface OpenWebAnsokanInfoBubbles {
  bubble_legitimeradfran: string;
  bubble_doc_personbevis: string;
  bubble_doc_sprakintyg: string;
  bubble_doc_examensbevis: string;
  bubble_doc_intygaktuellbehorighet: string;
  bubble_doc_intygnamnbyte: string;
  bubble_doc_artikel_11: string;
  bubble_doc_artikel_44_23: string;
  bubble_doc_artikel_43_43a_40_41_23: string;
  bubble_doc_artikel_24_23: string;
  bubble_doc_artikel_31_33_33a_23: string;
  bubble_doc_artikel_34_37_23: string;
  bubble_doc_artikel_3_dot_3: string;
  bubble_doc_dokumentfranutbildning: string;
  bubble_doc_kopiaspecialistutbildning: string;
  bubble_doc_kopiaforstaspecialistutbildning: string;
  bubble_doc_kopiaandraspecialistutbildning: string;
  bubble_doc_kopiaspecialistkompetensbevis: string;
  bubble_doc_kopiaforstaspecialistkompetensbevis: string;
  bubble_doc_kopiaandraspecialistkompetensbevis: string;
  bubble_doc_tjanstgoringsintyg: string;
  bubble_doc_kursplan?: string;
  bubble_doc_diplomasupplement?: string;
  bubble_doc_gymnasiebetyg?: string;
  bubble_doc_praktisktjanstgoring?: string;
  bubble_doc_svenskaforfattningar?: string;
  bubble_doc_ovrigt?: string;
}

// Needs to match:
// Legitimation.Models.Pocos.SkyddadYrkestitel.SkyddadYrkestitelOpenFormEpiTexts
export interface SkyddadYrkestitelOpenFormEpiTexts {
  BetalningHeading: string;
  BetalningPreamble: string;
  BetalningBody: string;
  BetalningBoxHeading: string;
  BetalningBoxBody: string;

  UtbildningHeading: string;
  UtbildningPreamble: string;
  UtbildningBody: string;

  DokumentHeading: string;
  DokumentPreamble: string;
  DokumentInforuta: string;
  DokumentBody: string;
  DokumentBoxHeading: string;
  DokumentBoxBody: string;

  PersonuppgifterHeading: string;
  PersonuppgifterPreamble: string;
  PersonuppgifterBody: string;

  SamtyckeLabel: string;
  ReadmorePersonuppgifter: string;
}

// Needs to match:
// Legitimation.Models.Pocos.SkyddadYrkestitel.SkyddadYrkestitelOpenFormInfoBubbles
export interface SkyddadYrkestitelOpenFormInfoBubbles {
  bubble_legitimeradfran: string;
  bubble_doc_personbevis: string;
  bubble_doc_sprakintyg: string;
  bubble_doc_examensbevis: string;
  bubble_doc_intygaktuellbehorighet: string;
  bubble_doc_intygnamnbyte: string;
  bubble_doc_dokumentfranutbildning: string;
}

/**
 * The 'Ja' values here should be updated if translations are changed.
 */
export const BUSINESS_RULES = {
  specialistkompetensEnabled: new Map<string | Yrke, string>([
    [Yrke.Lakare, 'Ja'],
    [Yrke.Tandlakare, 'Ja'],
  ]),
  specialistsjukskoterskaEnabled: new Map<string | Yrke, string>([[Yrke.Sjukskoterska, 'Ja']]),
};

export enum SpecialistsjukskoterskaArea {
  Ambulanssjukvard,
  Anestesisjukvard,
  Distriktsskoterska,
  HalsoOchSjukvardForBarnOchUngdomar,
  Intensivvard,
  KirurgiskVard,
  MedicinskVard,
  OnkologiskVard,
  Operationssjukvard,
  PsykiatriskVard,
  VardAvAldre,
  AnnanInriktning,
}

export const SPECIALISTSJUKSKOTERSKA_AREAS = [
  SpecialistsjukskoterskaArea.Ambulanssjukvard,
  SpecialistsjukskoterskaArea.Anestesisjukvard,
  SpecialistsjukskoterskaArea.Distriktsskoterska,
  SpecialistsjukskoterskaArea.HalsoOchSjukvardForBarnOchUngdomar,
  SpecialistsjukskoterskaArea.Intensivvard,
  SpecialistsjukskoterskaArea.KirurgiskVard,
  SpecialistsjukskoterskaArea.MedicinskVard,
  SpecialistsjukskoterskaArea.OnkologiskVard,
  SpecialistsjukskoterskaArea.Operationssjukvard,
  SpecialistsjukskoterskaArea.PsykiatriskVard,
  SpecialistsjukskoterskaArea.VardAvAldre,
  SpecialistsjukskoterskaArea.AnnanInriktning,
];

export class SectionClass<TProps extends SectionProps, TState extends SectionState> extends React.Component<
  TProps,
  TState
> {
  hasErrors(): boolean {
    return this.state?.errors?.size > 0;
  }

  getErrorMessage = (fieldName: string) => {
    return this.state.errors.get(fieldName)?.showMessage || this.props.submitButtonIsClicked
      ? this.state.errors.get(fieldName)?.errorMessage
      : null;
  };

  onChangeField = (field: FormField<any>, errors = new Map(this.state.errors)) => {
    const errorKey = validateField(field);
    let errorMessage = '';

    if (errorKey === '') {
      errors.delete(field.fieldName);
    } else {
      errorMessage = this.props.Translate(errorKey);
      errors.set(field.fieldName, { showMessage: true, errorMessage });
    }

    this.setState({ errors }, () => {
      this.props.onChangeFieldValue();
    });
  };

  getInitialErrorMap = (fields: string[]) => {
    const map = new Map<string, ValidationError>();
    fields.forEach((field) => {
      map.set(field, {
        showMessage: this.props.submitButtonIsClicked,
        errorMessage: this.props.Translate('error_mandatory'),
      });
    });
    return map;
  };

  getErrorSiblingClass = (field: FieldName, allOtherFields: FieldName[]) => {
    let numOfErrors = 0;
    allOtherFields.push(field);
    allOtherFields.forEach((fieldName) => {
      if (this.getErrorMessage(fieldName)) numOfErrors++;
    });
    if (numOfErrors === allOtherFields.length) return '';

    return this.getErrorMessage(field) ? 'error-sibling' : '';
  };
}

export interface SectionProps {
  onChangeFieldValue: () => void;
  Translate: (key: string, defaultValue?: string) => string;
  openWebAnsokanEpiTexts?: OpenWebAnsokanEpiTexts;
  skyddadYrkestitelOpenFormEpiTexts?: SkyddadYrkestitelOpenFormEpiTexts;
  skyddadYrkestitelOpenFormInfoBubbles?: SkyddadYrkestitelOpenFormInfoBubbles;
  isInEditMode?: boolean;
  formLanguage?: string;
  submitButtonIsClicked?: boolean;
  formType: FormType;
  isOutsideEuEesLessThanThreeYears?: boolean;
}

export interface SectionState {
  errors: Map<string, ValidationError>;
}

export enum FieldName {
  Yrke = 'yrke', //Backend relies on this value: \Legitimation\Legitimation\Global.cs; public const string Yrke = "yrke";
  AllaFornamn = 'allafornamn',
  Tilltalsnamn = 'tilltalsnamn',
  Efternamn = 'efternamn',
  Personnummer = 'personnummer',
  samordningsnummerOrBirthDate = 'samordningsnummer',

  Adress = 'adress',
  Postnummer = 'postnummer',
  Ort = 'ort',
  Land = 'land',
  Telefon = 'telefon',
  Epost = 'epost',
  EpostBekraftelse = 'epostbekraftelse',
  Kon = 'kon',
  OvrigInfo = 'ovriginfo',

  Utbildningsland = 'utbildningsland',
  Examenstitel = 'examenstitel',
  Examensar = 'examensar',
  Utbildningstid = 'utbildningstid',
  LegitimeradFran = 'legitimeradfran',
  HarLegIAnnatLand = 'harlegiannatland',
  AngeLand = 'angeland',
  AngeLandController = 'angelandController',
  Utbildningsform = 'utbildningsform',
  Utbildningsperiod = 'utbildningsperiod',

  AnsokerSpecialistsjukskoterska = 'ansokerspecialistsjukskoterska',
  JagAnsokerOmAtt = 'jagansokeromratt',
  SkrivDinInriktning = 'skrivdininriktning',
  SpecialExamensTitel = 'specialexamenstitel',
  SpecialExamensDatum = 'specialexamensdatum',
  SpecialDatum = 'specialdatum',
  SpecialLangd = 'speciallangd',
  SpecialUtbildningsLand = 'specialutbildningsland',

  AnsokerSpecialist = 'ansokerspecialist',
  TypAvSpecialist = 'typavspecialist',
  TypAvSpecialist_2 = 'typavspecialist_2',
  DatumSpecialist = 'datumspecialist',
  DatumSpecialist_2 = 'datumspecialist_2',
  SpecialLangd_2 = 'speciallangd_2',
  SpecialistUtbildningsland = 'specialistutbildningsland',
  SpecialistUtbildningsland_2 = 'specialistutbildningsland_2',
  HarSpecialistIAnnatLand = 'harspecialistiannatland',
  HarSpecialistIAnnatLand_2 = 'harspecialistiannatland_2',
  AngeSpecialistLand = 'angespecialistland',
  AngeSpecialistLandController = 'angespecialistlandController',
  AngeSpecialistLandController_2 = 'angespecialistlandController_2',
  AngeSpecialistLand_2 = 'angespecialistland_2',
  AnnanSpecialistbevistyp = 'annanspecialistbevistyp',
  AnnanSpecialistbevistyp_2 = 'annanspecialistbevistyp_2',
  LaddatOriginalDokument = 'laddatoriginaladokument',
  Samtycke = 'samtycke',

  PaidAssessmentFeeEarlier = 'paidAssessmentFeeEarlier',
  PaymentDate = 'paymentDate',

  AnsokerGranskning = 'ansokergranskning',
  GranskningUtbildningEfterGymnasiet = 'granskningutbildningeftergymnasiet',
  GranskningExamen = 'granskningexamen',
  GranskningUtbildningsplats = 'granskningutbildningsplats',
  GranskningYrkesutbildningLangd = 'granskningyrkesutbildninglangd',
  GranskningStartAr = 'granskningstartar',
  GranskningExamensAr = 'granskningexamensar',
  UtbildningslandGranskning = 'utbildningslandgranskning',
  GranskningArbetatEfterExamen = 'granskningarbetatefterexamen',
  GranskningTillatKontakt = 'granskningtillatkontakt',
  GranskningNejAnledning = 'granskningnejanledning',
  OmDuHaretEttTidigareArendeAngeDiariNummer = 'omduharetettidigarearendeangediarienummer',
}

export enum FormType {
  Legitimation = 'legitimation',
  SkyddadYrkestitel = 'skyddadyrkestitel',
}
